import { Link } from 'react-router-dom';
import { CalendarClock, CalendarDays, Check, MapPin, Search } from 'lucide-react';
import { CATALOG_TYPE_LABELS, LEVEL_LABELS, TOPICS, TOPIC_LABELS, type Topic } from '@ready/shared';
import type { CatalogItem } from '@/lib/api';
import { formatDate, formatSessionTime } from '@/lib/dates';
import { seatsText } from './event/learner-labels';
import { Card } from './ui';
import { OrgLogo } from './org-brand';

/** "45 min", "2 hours". */
export const formatMinutes = (m: number | null) =>
  m ? (m >= 90 ? `${Math.round(m / 60)} hours` : `${m} min`) : null;

const primaryLink =
  'rounded-[var(--radius)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90';
const secondaryLink =
  'rounded-[var(--radius)] border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted';

/**
 * One course, quiz or event, with the learner's progress and the next step.
 * Used on Home and in the catalog.
 */
export function CatalogCard({ item: i }: { item: CatalogItem }) {
  if (i.kind === 'event' && i.event) return <EventCard item={i} event={i.event} />;
  const meta = [
    LEVEL_LABELS[i.level],
    formatMinutes(i.minutes),
    i.kind === 'course'
      ? i.lessonCount && `${i.lessonCount} lesson${i.lessonCount === 1 ? '' : 's'}`
      : i.questionCount && `${i.questionCount} questions`,
  ].filter(Boolean);
  return (
    <Card className="flex h-full flex-col justify-between gap-4">
      <div className="min-w-0">
        <Source item={i} />
        <p className="mt-1 font-medium">{i.title}</p>
        {i.assignment && i.status !== 'completed' && <AssignedNote assignment={i.assignment} />}
        {i.summary && (
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{i.summary}</p>
        )}
        <p className="mt-2 text-xs text-muted-foreground">{meta.join(' · ')}</p>
      </div>
      {i.kind === 'course' && i.progress && i.status !== 'completed' && (
        <progress
          max={i.progress.required || 1}
          value={i.progress.completed}
          aria-label={`${i.title} progress`}
          className="h-1.5 w-full appearance-none overflow-hidden rounded-full [&::-moz-progress-bar]:bg-primary [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:bg-primary"
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Status item={i} />
        {i.kind === 'course' ? (
          <Link to={`/c/${i.slug}`} className={primaryLink}>
            {i.status === 'completed'
              ? 'Review'
              : i.status === 'in_progress'
                ? 'Continue'
                : 'View course'}
          </Link>
        ) : (
          <div className="flex items-center gap-2">
            <Link to={`/q/${i.slug}`} className={secondaryLink}>
              Details
            </Link>
            <Link to={`/q/${i.slug}?start=1`} className={primaryLink}>
              {i.attempts ? 'Retake' : 'Start'}
            </Link>
          </div>
        )}
      </div>
    </Card>
  );
}

/** "Course · Example University", with the org's logo when it has one. */
function Source({ item: i }: { item: CatalogItem }) {
  return (
    <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
      {i.fromLogoUrl && <OrgLogo name={i.from} url={i.fromLogoUrl} size="xs" />}
      <span className="truncate">
        {CATALOG_TYPE_LABELS[i.type]} · {i.from}
      </span>
    </p>
  );
}

type EventInfo = NonNullable<CatalogItem['event']>;

/** A workshop or webinar: when and where it is next, seats and the learner's registration. */
function EventCard({ item: i, event: e }: { item: CatalogItem; event: EventInfo }) {
  const next = e.nextSession;
  const over = !next || new Date(next.endsAt).getTime() < Date.now();
  const where = next
    ? next.format === 'online'
      ? 'Online'
      : next.format === 'hybrid'
        ? next.venue
          ? `${next.venue} and online`
          : 'Hybrid'
        : (next.venue ?? 'In person')
    : null;
  const signedUp = e.registration === 'registered' || e.registration === 'waitlisted';
  const seats = over || signedUp ? null : seatsText(e.seatsLeft);
  return (
    <Card className="flex h-full flex-col justify-between gap-4">
      <div className="min-w-0">
        <Source item={i} />
        <p className="mt-1 font-medium">{i.title}</p>
        {next && (
          <p className="mt-1 flex items-start gap-1.5 text-sm font-medium text-primary">
            <CalendarDays className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              {formatSessionTime(next.startsAt, next.endsAt)}
              {e.sessionCount > 1 && (
                <span className="font-normal text-muted-foreground">
                  {' '}
                  · {e.sessionCount} sessions
                </span>
              )}
            </span>
          </p>
        )}
        {where && (
          <p className="mt-1 flex items-start gap-1.5 text-sm text-muted-foreground">
            <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden />
            {where}
          </p>
        )}
        {i.summary && (
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{i.summary}</p>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          {[LEVEL_LABELS[i.level], seats].filter(Boolean).join(' · ')}
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <EventStatus item={i} event={e} over={over} />
        <Link to={`/e/${i.slug}`} className={signedUp || over ? secondaryLink : primaryLink}>
          {signedUp || over ? 'View' : 'View and register'}
        </Link>
      </div>
    </Card>
  );
}

function EventStatus({
  item: i,
  event: e,
  over,
}: {
  item: CatalogItem;
  event: EventInfo;
  over: boolean;
}) {
  if (i.status === 'completed')
    return (
      <span className="flex items-center gap-1 text-sm text-india-green">
        <Check className="size-4" aria-hidden /> Attended
      </span>
    );
  if (e.registration === 'registered')
    return (
      <span className="flex items-center gap-1 text-sm text-india-green">
        <Check className="size-4" aria-hidden /> Registered
      </span>
    );
  if (e.registration === 'waitlisted')
    return <span className="text-sm text-muted-foreground">Waitlisted</span>;
  return <span className="text-sm text-muted-foreground">{over ? 'Ended' : 'Not registered'}</span>;
}

function Status({ item: i }: { item: CatalogItem }) {
  if (i.kind === 'quiz' && i.best)
    return (
      <Link
        to={`/attempts/${i.best.attemptId}`}
        className="-my-2 py-2 text-sm text-muted-foreground hover:text-foreground"
      >
        Best: <span className="font-medium tabular-nums">{i.best.percent}%</span>
        {i.best.passed && ' · passed'}
      </Link>
    );
  if (i.status === 'completed')
    return (
      <span className="flex items-center gap-1 text-sm text-india-green">
        <Check className="size-4" aria-hidden /> Completed
      </span>
    );
  if (i.kind === 'course' && i.progress)
    return (
      <span className="text-sm text-muted-foreground">
        {i.progress.completed} of {i.progress.required} lessons
      </span>
    );
  return <span className="text-sm text-muted-foreground">Not started</span>;
}

/** "Assigned by Uni · Due October 30, 2026", in red once the date has passed. */
function AssignedNote({ assignment: a }: { assignment: NonNullable<CatalogItem['assignment']> }) {
  const late = a.dueAt && new Date(a.dueAt).getTime() < Date.now();
  return (
    <p
      className={`mt-1 flex items-center gap-1.5 text-xs font-medium ${late ? 'text-destructive' : 'text-primary'}`}
    >
      <CalendarClock className="size-3.5 shrink-0" aria-hidden />
      Assigned by {a.from}
      {a.dueAt && ` · ${late ? 'Was due' : 'Due'} ${formatDate(a.dueAt)}`}
    </p>
  );
}

/** A search box that submits (Enter or the button); the catalog searches as you type instead. */
export function SearchBox({
  value,
  onChange,
  onSubmit,
  placeholder = 'Search courses, events and more',
  label = 'Search',
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  label?: string;
}) {
  return (
    <form
      role="search"
      className="relative"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit?.();
      }}
    >
      <Search
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <input
        type="search"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 w-full rounded-[var(--radius)] border border-input bg-background pl-9 pr-3 text-base placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-sm"
      />
    </form>
  );
}

/**
 * Up to three catalog topics as toggle chips. `suggested` (e.g. from the
 * questions' skills) are marked so authors can pick them quickly.
 */
export function TopicPicker({
  value,
  onChange,
  suggested = [],
}: {
  value: Topic[];
  onChange: (topics: Topic[]) => void;
  suggested?: Topic[];
}) {
  const full = value.length >= 3;
  return (
    <div className="space-y-1.5">
      <p id="topics-label" className="text-sm font-medium">
        Topics <span className="font-normal text-muted-foreground">(up to three)</span>
      </p>
      <div className="flex flex-wrap gap-2" role="group" aria-labelledby="topics-label">
        {TOPICS.map((t) => {
          const on = value.includes(t.id);
          return (
            <button
              key={t.id}
              type="button"
              aria-pressed={on}
              disabled={!on && full}
              onClick={() => onChange(on ? value.filter((x) => x !== t.id) : [...value, t.id])}
              className={`min-h-9 rounded-full border px-3 text-sm disabled:opacity-40 ${
                on
                  ? 'border-primary bg-primary/15 font-medium text-foreground'
                  : 'border-border text-muted-foreground hover:bg-muted'
              }`}
            >
              {t.label}
              {!on && suggested.includes(t.id) && (
                <span className="ml-1 text-primary">· suggested</span>
              )}
            </button>
          );
        })}
      </div>
      {suggested.some((t) => !value.includes(t)) && !full && (
        <button
          type="button"
          className="mt-1 py-2 text-sm text-primary"
          onClick={() => onChange([...new Set([...value, ...suggested])].slice(0, 3) as Topic[])}
        >
          Use suggested: {suggested.map((t) => TOPIC_LABELS[t]).join(', ')}
        </button>
      )}
    </div>
  );
}
